import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { priceFormatter } from "@/lib/utils";
import { LIBRARIES_INDEX_PAGE, MYPAGE_PASSES, MYPAGE_SUBSCRIPTION, PASSES_LANDING } from "@/settings/constant";
import { styled } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef } from "react";
import { isSubscriptionExpiredError } from "src/api";
import { getRelatedProducts, Subjects } from "src/api/product";
import useHandoutProductStore from "src/store/relatedProduct";
import { usePurchaseStore } from "../purchaseStore";
import { useInvalidateEntitlementsQuery } from "../useEntitlementsQuery";
import { isSubscriptionInvalidResult, requestProductAvailability, requestProductUse } from "@/lib/entitlement/entitlementRequest";
const EmTitle = styled("em")((param)=>{
    let { theme  } = param;
    return {
        color: theme.palette.secondary.main,
        ...theme.typography.h6
    };
});
const EmContent = styled("em")((param)=>{
    let { theme  } = param;
    return {
        color: theme.palette.text.primary,
        ...theme.typography.subtitle1
    };
});
export const useLegacyPassLogic = (param)=>{
    let { product , skipConfirmation =false  } = param;
    const router = useRouter();
    const { openModal , closeModal , reset , selectedPass , setFlags  } = usePurchaseStore((state)=>({
            openModal: state.openModal,
            closeModal: state.closeModal,
            reset: state.reset,
            selectedPass: state.selectedPass,
            setFlags: state.setFlags,
            showCouponModal: state.showCouponModal
        }));
    const isProcessingRef = useRef(false);
    const usePassMutation = useMutation(requestProductUse);
    const availabilityMutation = useMutation(requestProductAvailability);
    const { refetch: refetchRelatedProducts  } = useQuery([
        "relatedProduct",
        product.id
    ], ()=>getRelatedProducts(product.id.toString()));
    const invalidateEntitlementsQuery = useInvalidateEntitlementsQuery();
    const { closePassSectionModal , updateMembershipAvailable , purchaseProductsInfo , hasAlreadyOwned , selectedPurchaseOption  } = useHandoutProductStore((state)=>({
            closePassSectionModal: state.closePassSectionModal,
            updateMembershipAvailable: state.updateMembershipAvailable,
            purchaseProductsInfo: state.purchaseProductsInfo,
            hasAlreadyOwned: state.hasAlreadyOwned,
            selectedPurchaseOption: state.selectedPurchaseOption
        }));
    const getModalContent = useCallback(()=>{
        const isAmount = selectedPass.deduction === "amount";
        return {
            title: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "자료 ",
                    /*#__PURE__*/ _jsxs(EmTitle, {
                        children: [
                            purchaseProductsInfo.totalCount,
                            "개"
                        ]
                    }),
                    "에 이용권을 사용할까요?"
                ]
            }),
            content: isAmount ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "구매 시 이용권에서 ",
                    /*#__PURE__*/ _jsxs(EmContent, {
                        children: [
                            priceFormatter(purchaseProductsInfo.totalPrice),
                            "원"
                        ]
                    }),
                    "를 차감해요."
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "사용 시 이용권에서 ",
                    /*#__PURE__*/ _jsxs(EmContent, {
                        children: [
                            purchaseProductsInfo.totalCount,
                            "회"
                        ]
                    }),
                    "를 차감해요."
                ]
            })
        };
    }, [
        selectedPass,
        purchaseProductsInfo
    ]);
    const showExceededLimitModal = useCallback((isAmount)=>{
        openModal({
            title: isAmount ? "이용권 사용금액을 초과했어요." : "이용권 사용횟수를 초과했어요.",
            content: isAmount ? "선택하신 자료의 금액이 이용권의 남은 금액을 초과했어요." : "선택하신 상품의 갯수가 이용권의 남은 횟수를 초과했어요.",
            severity: "caution",
            prefix: null,
            suffix: {
                variant: "solid",
                label: "확인",
                handleClick: ()=>{
                    closeModal();
                    mixpanelEvent("Pass Unavailable", {
                        "Popup Title": isAmount ? "이용권 사용금액을 초과했어요." : "이용권 사용횟수를 초과했어요.",
                        "Button Name": "확인",
                        "Button URL": null
                    });
                }
            }
        });
    }, [
        openModal,
        closeModal
    ]);
    /**
   * 구독이 무효(결제 실패·만료)라 담을 수 없을 때의 안내.
   *
   * 사전 판정(`subscription-available` 의 `SUBSCRIPTION_EXPIRED`)과 담기 요청의 403 이
   * 같은 상황을 서로 다른 시점에 알려주므로 문구를 공유한다. 문구는 자료보관함과 동일하다.
   */ const showSubscriptionExpiredModal = useCallback(()=>{
        openModal({
            title: "결제가 완료되지 않았어요.",
            content: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "잔액 부족 등의 이유로 이번 달 구독 결제가 실패했어요.",
                    /*#__PURE__*/ _jsx("br", {}),
                    "결제를 완료하면 보유 중인 자료를 다시 이용할 수 있어요."
                ]
            }),
            severity: "caution",
            prefix: {
                variant: "soft",
                label: "닫기",
                handleClick: closeModal
            },
            suffix: {
                variant: "solid",
                label: "결제 완료하기",
                handleClick: ()=>{
                    router.push(MYPAGE_SUBSCRIPTION);
                }
            }
        });
    }, [
        openModal,
        closeModal,
        router
    ]);
    const validateDeductionLimits = useCallback(()=>{
        // 무제한 수단은 한도 개념이 없어 검증하지 않는다.
        // 구독은 availableAmount 가 상시 null 이라 deduction 이 항상 'count' 로 파생되므로
        // 아래 금액 분기에 도달하지 않는다 — 별도 분기가 필요 없다
        if (!selectedPass.skipLimitCheck) {
            const isAmount = selectedPass.deduction === "amount";
            if (isAmount && selectedPass.availableAmount < purchaseProductsInfo.totalPrice) {
                showExceededLimitModal(true);
                return false;
            } else if (!isAmount && selectedPass.availableCount < purchaseProductsInfo.totalCount) {
                showExceededLimitModal(false);
                return false;
            }
        }
        return true;
    }, [
        selectedPass,
        purchaseProductsInfo,
        showExceededLimitModal
    ]);
    // 연관자료 없이 현재 상품만 사용하려는 경우
    const currentProductUsePass = useCallback(async ()=>{
        if (hasAlreadyOwned) {
            openModal({
                title: "이미 이용 중이에요.",
                content: "해당 자료는 자료 보관함에서 확인할 수 있어요.",
                suffix: {
                    variant: "solid",
                    label: "자료 보관함 이동",
                    handleClick: ()=>{
                        var _product_source;
                        mixpanelEvent("Popup Button Clicked", {
                            //팝업 mx-9
                            "Popup Type": "Already Owned Product",
                            "Popup Title": "이미 이용 중이에요.",
                            "Button Name": "자료 보관함 이동",
                            "Button URL": LIBRARIES_INDEX_PAGE
                        });
                        var _product_source_subject;
                        router.push("".concat(LIBRARIES_INDEX_PAGE, "?subject=").concat((_product_source_subject = (_product_source = product.source) === null || _product_source === void 0 ? void 0 : _product_source.subject) !== null && _product_source_subject !== void 0 ? _product_source_subject : Subjects.EN));
                    }
                }
            });
            return;
        }
        if (!validateDeductionLimits()) return;
        // skipConfirmation이 true인 경우, 모달을 띄우지 않고 바로 사용처리
        if (skipConfirmation) {
            usePassConfirmed();
            return;
        }
        const { title , content  } = getModalContent();
        openModal({
            title,
            content,
            prefix: {
                variant: "soft",
                label: "취소",
                handleClick: ()=>{
                    closeModal();
                }
            },
            suffix: {
                variant: "solid",
                label: "이용권 사용하기",
                handleClick: ()=>{
                    usePassConfirmed();
                }
            }
        });
    }, [
        hasAlreadyOwned,
        openModal,
        router,
        product,
        closeModal,
        getModalContent,
        validateDeductionLimits,
        skipConfirmation
    ]);
    const relatedProductUsePass = useCallback(async ()=>{
        if (!validateDeductionLimits()) return;
        const productIds = purchaseProductsInfo.selectedRelatedProducts.map((p)=>p.id.toString());
        // 이용권·구독 모두 사전 판정을 거친다. 엔드포인트와 응답 형태 차이는 requestProductAvailability 가 흡수한다
        const availabilities = await availabilityMutation.mutateAsync({
            entitlement: selectedPass,
            productIds,
            choicePurchaseOption: selectedPurchaseOption === "download_origin" ? "download" : selectedPurchaseOption
        });
        // 구독 자체가 무효면 어떤 자료를 빼도 담기지 않는다. 자료를 골라 보여주는 안내 대신 결제로 보낸다
        if (isSubscriptionInvalidResult(availabilities)) {
            showSubscriptionExpiredModal();
            return;
        }
        const hasUnusableProduct = availabilities.filter((item)=>!item.usable).map((item)=>item.productId);
        if (hasAlreadyOwned) {
            if (hasUnusableProduct.length > 0) {
                updateMembershipAvailable(hasUnusableProduct);
                setFlags({
                    showAvailableRelatedProductsModal: true
                });
                closePassSectionModal();
                return;
            }
            updateMembershipAvailable([]);
            setFlags({
                showAvailableRelatedProductsModal: true
            });
            closePassSectionModal();
            return;
        }
        if (hasUnusableProduct.length > 0) {
            updateMembershipAvailable(hasUnusableProduct);
            setFlags({
                showAvailableRelatedProductsModal: true
            });
            closePassSectionModal();
            return;
        }
        if (skipConfirmation) {
            usePassConfirmed();
            return;
        }
        const { title , content  } = getModalContent();
        openModal({
            title,
            content,
            prefix: {
                variant: "soft",
                label: "취소",
                handleClick: ()=>{
                    closeModal();
                }
            },
            suffix: {
                variant: "solid",
                label: "이용권 사용하기",
                handleClick: ()=>{
                    usePassConfirmed();
                }
            }
        });
    }, [
        purchaseProductsInfo,
        availabilityMutation,
        selectedPurchaseOption,
        showSubscriptionExpiredModal,
        updateMembershipAvailable,
        hasAlreadyOwned,
        openModal,
        closeModal,
        closePassSectionModal,
        selectedPass,
        setFlags,
        getModalContent,
        validateDeductionLimits,
        skipConfirmation
    ]);
    const handleUsePass = useCallback(async ()=>{
        // mx-3,18
        mixpanelEvent("Purchase Button Clicked", {
            PurchaseType: "이용권 사용",
            "Pass ID": selectedPass.id,
            "Pass Title": selectedPass.title,
            "Product IDs": purchaseProductsInfo.selectedRelatedProducts.map((p)=>p.id).concat([
                product.id.toString()
            ]),
            "Product Titles": purchaseProductsInfo.selectedRelatedProducts.map((p)=>p.title).concat([
                product.title
            ]),
            "Purchase option": selectedPurchaseOption
        });
        if (purchaseProductsInfo.selectedRelatedProducts.length === 0) {
            currentProductUsePass();
        } else {
            relatedProductUsePass();
        }
    }, [
        selectedPass,
        purchaseProductsInfo,
        product,
        currentProductUsePass,
        relatedProductUsePass,
        selectedPurchaseOption
    ]);
    // 이용권 사용하기 버튼 클릭 후
    const usePassConfirmed = useCallback(()=>{
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;
        const productIds = hasAlreadyOwned ? purchaseProductsInfo.selectedRelatedProducts.map((p)=>p.id.toString()) : purchaseProductsInfo.selectedRelatedProducts.map((p)=>p.id.toString()).concat(product.id.toString());
        usePassMutation.mutate({
            entitlement: selectedPass,
            productIds,
            choicePurchaseOption: selectedPurchaseOption === "download_origin" ? "download" : selectedPurchaseOption
        }, {
            onSuccess: async (data)=>{
                var _selectedPass_tracking, _selectedPass_tracking1, _selectedPass_tracking2;
                isProcessingRef.current = false;
                invalidateEntitlementsQuery(product.id);
                openModal({
                    title: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            "자료 보관함에 자료 ",
                            /*#__PURE__*/ _jsxs(EmTitle, {
                                children: [
                                    productIds.length,
                                    "개"
                                ]
                            }),
                            "를 추가했어요."
                        ]
                    }),
                    content: "내 자료들은 자료 보관함에서 확인할 수 있어요.",
                    severity: "success",
                    suffix: {
                        variant: "solid",
                        label: "자료 보관함 가기",
                        handleClick: ()=>{
                            var _product_source;
                            //mx-9
                            mixpanelEvent("Popup Button Clicked", {
                                "Popup Type": "Use Pass",
                                "Popup Title": "자료 보관함에 자료를 추가했어요.",
                                "Button Name": "자료 보관함 이동",
                                "Button URL": LIBRARIES_INDEX_PAGE
                            });
                            var _product_source_subject;
                            router.push("".concat(LIBRARIES_INDEX_PAGE, "?subject=").concat((_product_source_subject = (_product_source = product.source) === null || _product_source === void 0 ? void 0 : _product_source.subject) !== null && _product_source_subject !== void 0 ? _product_source_subject : Subjects.EN));
                        }
                    },
                    prefix: {
                        variant: "soft",
                        label: "닫기",
                        handleClick: ()=>{
                            closeModal();
                            mixpanelEvent("Popup Button Clicked", {
                                "Popup Type": "Use Pass",
                                "Popup Title": "자료 보관함에 자료를 추가했어요.",
                                "Button Name": "닫기",
                                "Button URL": null
                            });
                        }
                    }
                });
                await refetchRelatedProducts();
                var _selectedPass_tracking_category, _selectedPass_tracking_categoryTitle, _selectedPass_tracking_category1;
                // mx-15
                mixpanelEvent("Pass Use Completed", {
                    "Pass ID": selectedPass.id,
                    // GET /entitlements 응답에 category 가 없어 현재는 null 로 나간다 (plan.md §4-1)
                    "Pass Category": (_selectedPass_tracking_category = (_selectedPass_tracking = selectedPass.tracking) === null || _selectedPass_tracking === void 0 ? void 0 : _selectedPass_tracking.category) !== null && _selectedPass_tracking_category !== void 0 ? _selectedPass_tracking_category : null,
                    "Pass Category Title": (_selectedPass_tracking_categoryTitle = (_selectedPass_tracking1 = selectedPass.tracking) === null || _selectedPass_tracking1 === void 0 ? void 0 : _selectedPass_tracking1.categoryTitle) !== null && _selectedPass_tracking_categoryTitle !== void 0 ? _selectedPass_tracking_categoryTitle : null,
                    "Pass Title": selectedPass.title,
                    "Product ID": product.id,
                    "Product Category": product.category,
                    "Product Title": product.title,
                    "Product Price": product.price,
                    "Author ID": product.author,
                    // NOTE: 기존 코드 그대로. category 가 Author Name 으로 들어가는 것은 의도 확인 필요
                    "Author Name": (_selectedPass_tracking_category1 = (_selectedPass_tracking2 = selectedPass.tracking) === null || _selectedPass_tracking2 === void 0 ? void 0 : _selectedPass_tracking2.category) !== null && _selectedPass_tracking_category1 !== void 0 ? _selectedPass_tracking_category1 : null,
                    "Purchase option": selectedPurchaseOption
                });
            },
            onError: async (error)=>{
                isProcessingRef.current = false;
                if (error.response.status === 409 && error.response.data.message.includes("could not purchase or use the product")) {
                    openModal({
                        title: "해당 상품은 판매가 중단됐어요.",
                        content: "저작권 이용기간이 만료된 상품이거나 판매가 중지된 상품이에요.",
                        severity: "caution",
                        suffix: null
                    });
                }
                if (error.response.status === 409 && error.response.data.message.includes("no remains")) {
                    const isAmount = selectedPass.deduction === "amount";
                    showExceededLimitModal(isAmount);
                    return;
                }
                if (error.response.status === 409 && error.response.data.message.includes("user already has a product")) {
                    openModal({
                        title: "이미 이용중인 자료가 있어요",
                        content: "해당 자료를 제외한 자료만 이용권 차감돼요",
                        severity: "caution",
                        suffix: {
                            variant: "solid",
                            label: "자료 보관함 바로가기",
                            handleClick: ()=>{
                                router.push(LIBRARIES_INDEX_PAGE);
                            }
                        }
                    });
                    await refetchRelatedProducts();
                }
                if (error.response.status === 403) {
                    // 같은 403 이지만 사유가 둘이다. 월구독이 무효(결제 실패·만료)라 이용권 사용이 막힌 경우와,
                    // 애초에 이용권으로 이용할 수 없는 상품인 경우. 앞의 경우에 아래 구매 유도 문구가 나가면
                    // 이미 구독을 결제한 유저에게 "이용권을 사세요" 라는 엉뚱한 안내가 된다.
                    if (isSubscriptionExpiredError(error)) {
                        showSubscriptionExpiredModal();
                        return;
                    }
                    openModal({
                        title: "이용권을 구매해주세요!",
                        content: "해당 자료는 이용권으로 결제할 수 있어요.",
                        severity: "caution",
                        suffix: {
                            variant: "solid",
                            label: "이용권 구매하기",
                            handleClick: ()=>{
                                // 구 /passes/order 는 id 없이 push 돼 상품 조회가 깨졌다. 이용권 목록으로 보낸다.
                                router.push(PASSES_LANDING);
                            }
                        }
                    });
                }
                // solvookplus, solvookplus-subscribe,쏠플의 경우 이용권으로 다운로드 사용 불가에 대한 에러
                if (error.response.status === 422) {
                    openModal({
                        title: "이용권의 사용 범위와 맞지 않는 자료에요.",
                        content: "마이페이지 > 이용권 관리에서 선택한 이용권의 사용 범위를 확인해주세요.",
                        severity: "caution",
                        suffix: {
                            variant: "solid",
                            label: "확인",
                            handleClick: ()=>{
                                mixpanelEvent("Popup Button Clicked", {
                                    "Popup Type": "Pass Use Failed",
                                    "Popup Title": "이용권의 사용 범위와 맞지 않는 자료에요.",
                                    "Button Name": "확인",
                                    "Button URL": null
                                });
                                closeModal();
                            }
                        },
                        prefix: {
                            variant: "soft",
                            label: "이용권 관리 가기",
                            handleClick: ()=>{
                                mixpanelEvent("Popup Button Clicked", {
                                    "Popup Type": "Pass Use Failed",
                                    "Popup Title": "이용권의 사용 범위와 맞지 않는 자료에요.",
                                    "Button Name": "이용권 관리 가기",
                                    "Button URL": MYPAGE_PASSES
                                });
                                router.push(MYPAGE_PASSES);
                            }
                        }
                    });
                }
            }
        });
    }, [
        selectedPass,
        purchaseProductsInfo,
        product,
        usePassMutation,
        refetchRelatedProducts,
        router,
        hasAlreadyOwned,
        openModal,
        closeModal,
        showExceededLimitModal,
        showSubscriptionExpiredModal,
        selectedPurchaseOption
    ]);
    useEffect(()=>{
        return ()=>{
            reset();
        };
    }, []);
    return {
        // 이용권 사용 프로세스 시작하는 메서드. 단일 상품 + 연관 상품에 대한 로직 처리
        handleUsePass,
        // 사용을 확정하는 메서드. 사용 요청을 보내고 결과에 따른 모달 처리
        usePassConfirmed
    };
};
